import { FormEvent, useEffect, useMemo, useRef, useState } from "react";

import AppShell from "../components/AppShell";
import SharingTabs from "../components/SharingTabs";
import { CardSkeleton } from "../components/Skeleton";
import { listAgents, type AgentRow } from "../api/agents";
import {
  createTransfer,
  listTransfers,
  type TransferRow,
} from "../api/transfers";

const POLL_INTERVAL_MS = 5_000;
const MAX_BYTES = 50 * 1024 * 1024;

export default function SendFile() {
  const [agents, setAgents] = useState<AgentRow[] | null>(null);
  const [rows, setRows] = useState<TransferRow[] | null>(null);
  const [recipientId, setRecipientId] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  // Agents loaded once; the transfer list polls so status updates
  // (pending → sent / failed) show without a manual refresh.
  useEffect(() => {
    let cancelled = false;
    listAgents()
      .then((data) => {
        if (cancelled) return;
        // Any installed PC can receive — an offline one just gets the file
        // queued until it wakes. List online first for convenience.
        const usable = data.results
          .filter((a) => a.status !== "uninstalled" && a.status !== "blocked")
          .sort((a, b) => (a.status === "online" ? -1 : 0) - (b.status === "online" ? -1 : 0));
        setAgents(usable);
        if (usable.length > 0) setRecipientId(usable[0].id);
      })
      .catch((e) => {
        if (!cancelled) setFormError(extractError(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function fetchOnce() {
      try {
        const data = await listTransfers();
        if (!cancelled) {
          setRows(data.results);
          setListError(null);
        }
      } catch (e) {
        if (!cancelled) setListError(extractError(e, "Couldn't load recent sends."));
      }
    }
    fetchOnce();
    const id = setInterval(fetchOnce, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const recipientName = useMemo(
    () => agents?.find((a) => a.id === recipientId),
    [agents, recipientId],
  );

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    setFormError(null);
    const f = e.target.files?.[0] ?? null;
    if (f && f.size > MAX_BYTES) {
      setFormError("That file is too big to send. The limit is 50 MB.");
      setFile(null);
      return;
    }
    setFile(f);
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!file || !recipientId) return;
    setSubmitting(true);
    setFormError(null);
    setNotice(null);
    try {
      const result = await createTransfer(recipientId, file);
      const recipient = recipientName?.label || recipientName?.hostname || "that computer";
      setNotice(
        result.queued
          ? `Queued for ${recipient} — it's asleep right now, so the file delivers when it wakes.`
          : `Sending ${result.filename} to ${recipient}…`,
      );
      setFile(null);
      if (fileInput.current) fileInput.current.value = "";
      const data = await listTransfers();
      setRows(data.results);
    } catch (e) {
      setFormError(extractError(e));
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = recipientId !== "" && file !== null && !submitting;

  return (
    <AppShell>
      <div className="mx-auto max-w-4xl">
        <h1 className="text-2xl font-semibold text-slate-900">Sharing</h1>
        <p className="mt-1 text-slate-600">
          Share folders and printers with everyone in the office.
        </p>
        <SharingTabs />

        <div>
          <h2 className="text-lg font-semibold text-slate-900">Send a file</h2>
          <p className="mt-1 text-sm text-slate-600">
            Pick a file and send it to any computer in the office. It lands in that computer's
            <span className="ml-1 font-mono text-xs text-slate-700">
              KraetdNet Inbox
            </span>
            {" "}folder. Works even if you're on your phone.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="mt-6 space-y-5 rounded-2xl border border-slate-200 bg-white p-5"
        >
          <div>
            <label className="block text-sm font-medium text-slate-700" htmlFor="file">
              Which file?
            </label>
            <input
              id="file"
              ref={fileInput}
              type="file"
              onChange={onPickFile}
              className="mt-1 w-full rounded-lg border border-slate-300 p-3 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-slate-900 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white"
            />
            {file ? (
              <p className="mt-1 text-xs text-slate-500">
                {file.name} · {formatBytes(file.size)}
              </p>
            ) : (
              <p className="mt-1 text-xs text-slate-500">Up to 50 MB.</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700" htmlFor="recipient">
              Send to which computer?
            </label>
            {agents === null ? (
              <p className="mt-1 text-sm text-slate-500">Loading…</p>
            ) : agents.length === 0 ? (
              <p className="mt-1 text-sm text-amber-900">No computers in your office yet.</p>
            ) : (
              <select
                id="recipient"
                value={recipientId}
                onChange={(e) => setRecipientId(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 p-3"
                required
              >
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label || a.hostname}
                    {a.status === "online" ? "" : " (offline — delivers when it wakes)"}
                  </option>
                ))}
              </select>
            )}
          </div>

          {notice ? (
            <p className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-900">{notice}</p>
          ) : null}
          {formError ? (
            <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">
              {formError}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full rounded-lg bg-slate-900 p-3 font-medium text-white disabled:opacity-50"
          >
            {submitting ? "Sending…" : "Send file"}
          </button>
        </form>

        <div className="mt-10">
          <h2 className="text-lg font-semibold text-slate-900">Recent sends</h2>
          <p className="mt-1 text-sm text-slate-600">Updates automatically.</p>

          {listError ? (
            <p role="alert" className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-800">
              {listError}
            </p>
          ) : null}

          <div className="mt-4">
            {rows === null ? (
              <CardSkeleton count={2} />
            ) : rows.length === 0 ? (
              <EmptyState />
            ) : (
              <ul className="space-y-3">
                {rows.map((row) => (
                  <TransferCard key={row.id} row={row} />
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function TransferCard({ row }: { row: TransferRow }) {
  return (
    <li className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-base font-semibold text-slate-900">{row.filename}</div>
          <div className="mt-1 break-words text-sm text-slate-500">
            To <span className="font-medium">{row.recipient_label || row.recipient_hostname}</span>
          </div>
          {row.status === "sent" && row.bytes_sent != null ? (
            <div className="mt-1 text-xs text-slate-500">
              {formatBytes(row.bytes_sent)} · {formatTimeAgo(row.completed_at || row.created_at)}
            </div>
          ) : (
            <div className="mt-1 text-xs text-slate-500">
              Started {formatTimeAgo(row.created_at)}
            </div>
          )}
          {row.status === "failed" && row.error_message ? (
            <p role="alert" className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-800">
              {row.error_message}
            </p>
          ) : null}
        </div>
        <StatusBadge status={row.status} />
      </div>
    </li>
  );
}

function StatusBadge({ status }: { status: TransferRow["status"] }) {
  const map: Record<TransferRow["status"], { label: string; className: string }> = {
    pending: { label: "Sending…", className: "bg-amber-100 text-amber-900" },
    running: { label: "Sending…", className: "bg-amber-100 text-amber-900" },
    sent: { label: "Delivered", className: "bg-emerald-100 text-emerald-900" },
    failed: { label: "Failed", className: "bg-red-100 text-red-900" },
  };
  const meta = map[status];
  return (
    <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium ${meta.className}`}>
      {meta.label}
    </span>
  );
}

function EmptyState() {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
      <h2 className="text-lg font-semibold text-slate-900">No files sent yet</h2>
      <p className="mt-2 text-sm text-slate-600">
        Use the form above to send your first file across the office.
      </p>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatTimeAgo(iso: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hr ago`;
  return `${Math.floor(seconds / 86400)} days ago`;
}

function extractError(
  e: unknown,
  fallback = "Couldn't send that file. Try again.",
): string {
  if (typeof e === "object" && e && "response" in e) {
    const response = (e as { response?: { data?: { detail?: string } } }).response;
    if (response?.data?.detail) return response.data.detail;
  }
  return fallback;
}
